import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Dialog } from "@/components/common/Dialog";
import { logout } from "@/auth/actions";
import { useMe } from "@/auth/use-me";
import { DeletePersonDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

// Settings · Konto — irreversible account deletion (genesis deletePerson).
// Mirrors discovery_app: the user must type their full name to confirm, then
// the session is ended (logout → redirect). Lives in a "danger zone" card.
export function DeleteAccountCard() {
  const intl = useIntl();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Sticky on purpose: the mutation's own `loading` drops back to false the
  // moment the account is gone, which would re-arm the dialog for the whole
  // teardown that follows - and deleting an already-deleted account reports a
  // failure for something that succeeded (#242).
  const [busy, setBusy] = useState(false);
  const [deletePerson] = useMutation(DeletePersonDocument);

  const expectedName = me.fullName.trim();
  const canDelete =
    expectedName.length > 0 &&
    confirmText.trim().toLowerCase() === expectedName.toLowerCase() &&
    Boolean(me.id);

  const scope = intl.formatMessage({
    id: "settings.account.delete.desc",
    defaultMessage:
      "Your profile data, documents, contacts and groups are deleted and your login is revoked. This cannot be undone.",
  });

  function openDialog() {
    setConfirmText("");
    setError(null);
    setOpen(true);
  }

  async function confirmDelete() {
    if (!canDelete || !me.id || busy) return;
    setError(null);
    setBusy(true);
    try {
      const res = await deletePerson({ variables: { input: { id: me.id, dryRun: false } } });
      if (!res.data?.deletePerson?.id) throw new Error("deletePerson returned no id");
    } catch (err) {
      // Only the mutation itself lands here, which is the one case where a
      // retry can still succeed (#242). The account is untouched, so the
      // dialog goes live again.
      setError(userErrorMessage(intl, err));
      setBusy(false);
      return;
    }
    // A returned id means the account is gone; what follows is teardown of our
    // own session, and it never reports back - genesis revokes the login as
    // part of the deletion, so a stumble here must not read as a failed
    // deletion. logout() ends on the login screen on every path.
    await logout();
  }

  return (
    <div className="box bg-surface p-6">
      <div className="text-smycles-micro tracking-[0.25em] text-red-600/80">
        {intl.formatMessage({ id: "settings.account.delete.zone", defaultMessage: "Danger zone" })}
      </div>
      <div className="mt-3 flex flex-col gap-3 @sm:flex-row @sm:items-center @sm:justify-between">
        <div className="max-w-md">
          <div className="smycles-field-value font-medium">
            {intl.formatMessage({ id: "settings.account.delete.title", defaultMessage: "Delete account" })}
          </div>
          <div className="mt-1 text-smycles-meta text-ink-muted">
            {scope}
          </div>
        </div>
        <Button variant="destructive" onClick={openDialog} className="shrink-0">
          {intl.formatMessage({ id: "settings.account.delete.button", defaultMessage: "Delete account" })}
        </Button>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        preventClose={busy}
        title={intl.formatMessage({
          id: "settings.account.delete.dialogTitle",
          defaultMessage: "Permanently delete account?",
        })}
      >
        <p className="text-smycles-body text-ink">{scope}</p>
        <p className="mt-2 text-smycles-body text-ink-muted">
          {intl.formatMessage({
            id: "settings.account.delete.retained",
            defaultMessage:
              "Some content stays: chat messages, conversation history, published contributions and match and assessment results remain, no longer attached to your name.",
          })}
        </p>
        <label className="mt-4 block text-smycles-meta text-ink">
          {intl.formatMessage({
            id: "settings.account.delete.prompt",
            defaultMessage: "Type your full name to confirm:",
          })}{" "}
          <strong className="text-ink">{expectedName || "–"}</strong>
        </label>
        <input
          autoFocus
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={intl.formatMessage({
            id: "settings.account.delete.placeholder",
            defaultMessage: "Full name",
          })}
          className="mt-2 w-full rounded-md bg-page px-3 py-2 text-smycles-body outline-none ring-1 ring-ink/10 transition focus:ring-ink-accent-ui"
        />
        {error ? <div className="mt-2 text-smycles-meta text-red-600">{error}</div> : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={() => setOpen(false)}>
            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
          <Button
            variant="destructive"
            disabled={!canDelete || busy}
            onClick={() => void confirmDelete()}
            className="disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy
              ? intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })
              : intl.formatMessage({
                  id: "settings.account.delete.confirm",
                  defaultMessage: "Delete permanently",
                })}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
